/**
 * Wrapper function for importScene to handle file upload
 * @param {File} file - The file to import
 * @returns {Promise} - A promise that resolves when the import is complete
 */
const handleImportScene = (file) => {
  return importScene(file)
} // src/Planner.jsx
import React, { useState, useEffect, useRef, useMemo } from "react"
import logo from "./assets/logo-nudesign.png"
import logoNu from "./assets/logo-nu.png"
import { FaChevronDown, FaChevronRight, FaSpinner } from "react-icons/fa"

import placeholder from "./assets/placeholder.jpg"

// Material manager and components
import { materialManager } from "./materialManager"
import { Accordion } from "./Accordion"
// FamilySelector removed - now using only Category + Model like ProductSelector
import PlannerFamilyAccordion from "./PlannerFamilyAccordion" // Planner-specific version

// Configuration functions
import { switchConfiguration } from "./config"

// API functions
import { fetchProducts, fetchMaterialLinks } from "./api"

// Local product list (for matching with server data)
import products from "./products"
import PlannerPlayer from "./PlannerPlayer"
import PlannerProductsList from "./components/PlannerProductsList"
import PlannerActions from "./components/PlannerActions"
import PlannerCategorySelector from "./components/PlannerCategorySelector"
import { IoClose } from "react-icons/io5"
import { getTranslations } from "./components/translations"

// 5° rotation steps (changed from 15° to 5°)
const angles = Array.from({ length: 73 }, (_, i) => i * 5)
const Y_OFFSET = 1.0

// Bilingual labels
const labels = {
  selectModel: "Seleziona modello / Select model",
  allProducts: "TUTTI I PRODOTTI / ALL PRODUCTS",
  floor: {
    length: "L",
    width: "W",
    height: "H",
  },
  addedProducts: "Prodotti aggiunti / Added products",
  noProducts: "Nessun prodotto aggiunto / No products added yet",
  materials: "Materiali / Materials",
  upholstery: "Rivestimento / Upholstery",
  structure: "Struttura / Structure",
  finish: "Finitura / Finish",
  searchPlaceholder: "Cerca... / Search...",
  allCategories: "TUTTE LE CATEGORIE / ALL CATEGORIES",
  noConfigurableMaterials:
    "Nessun materiale configurabile / No configurable materials",
  noMaterialsAvailable: "Nessun materiale disponibile / No materials available",
  designSpace: "PROGETTA SPAZIO / DESIGN SPACE", // New label for SET
}

// Local storage keys
const LS_KEYS = {
  CATEGORIES: "lacividina_categories",
  PRODUCTS_LIST: "lacividina_products_list",
  FAMILIES: "lacividina_families",
  LAST_FETCH: "lacividina_last_fetch",
}

/**
 * Parse URL to get preset parameter
 * Supports formats:
 * - #/planner?preset=filename.json
 * - #/planner?preset=filename (without .json extension)
 * @returns {string|null} Preset filename or null
 */
const getPresetFromUrl = () => {
  const hash = window.location.hash
  const queryIndex = hash.indexOf("?")
  if (queryIndex === -1) return null

  const queryString = hash.substring(queryIndex + 1)
  const params = new URLSearchParams(queryString)
  const preset = params.get("preset")

  if (!preset) return null

  // Add .json extension if not present
  return preset.endsWith(".json") ? preset : `${preset}.json`
}

// Cache timeout in milliseconds (24 hours)
const CACHE_TIMEOUT = 24 * 60 * 60 * 1000

/**
 * Main Planner logic
 */
const Planner = () => {
  const iframeRef = useRef(null)
  const emptyImageRef = useRef(null)
  const nodeLocalIdsRef = useRef(1)
  const plannerPlayerRef = useRef(null) // Ref to call PlannerPlayer functions directly
  const [viewerMaterials, setViewerMaterials] = useState([])

  const [loading, setLoading] = useState(true)
  const [loadingProducts, setLoadingProducts] = useState({})

  // Language state (default Italian)
  const [language, setLanguage] = useState("it")

  // "Projects" data from viewer
  const [categories, setCategories] = useState([])
  const [productsList, setProductsList] = useState([]) // productNodes from getPlannerUsableProjects

  // Family and product selection (for UI)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedFamily, setSelectedFamily] = useState(null)
  const [allProducts, setAllProducts] = useState([]) // All products from API
  const [materialLinks, setMaterialLinks] = useState([]) // Material links from API

  // State of ProductNode instances in scene
  const [productNodes, setProductNodes] = useState({})
  const [productNodesToAdd, setProductNodesToAdd] = useState({})
  const [selectedProductLocalId, setSelectedProductLocalId] = useState(null)

  // Selected product node for material changes
  const [selectedProductNode, setSelectedProductNode] = useState(null)

  const [ignoreClick, setIgnoreClick] = useState(false)

  // UI states and toggles
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [floorMaterial, setFloorMaterial] = useState("piano")
  const [floorDimensions, setFloorDimensions] = useState({
    length: 2500.0,
    width: 2500.0,
    height: 0.1,
  })

  // For managing the expansion/closing of material accordions
  const [accordionOpenState, setAccordionOpenState] = useState({})

  // To track the Emersya instance (fix for emViewers access)
  const [emersyaViewerInstance, setEmersyaViewerInstance] = useState(null)

  // Track if URL preset has been loaded
  const presetLoadedRef = useRef(false)
  const [isLoadingPreset, setIsLoadingPreset] = useState(false)

  // Get translations
  const translations = getTranslations(language)

  /**
   * Resets the scene by removing all product nodes
   */
  const resetScene = () => {
    // Remove all product nodes from the Emersya viewer
    Object.keys(productNodes).forEach((localId) => {
      iframeRef.current.contentWindow.postMessage(
        {
          action: "removeProductNodeInstance",
          localId: parseInt(localId),
          id: parseInt(localId),
        },
        "*",
      )
    })

    // Clear the state
    setProductNodes({})
    setProductNodesToAdd({})
    setLoadingProducts({})
    setSelectedProductLocalId(null)
  }

  /**
   * Load a preset from the assets folder via URL parameter
   * @param {string} presetFilename - The filename of the preset (e.g., "planner-preset.json")
   */
  const loadPresetFromUrl = async (presetFilename) => {
    if (!presetFilename || presetLoadedRef.current) return

    console.log(`[PLANNER] Loading preset from URL: ${presetFilename}`)
    setIsLoadingPreset(true)
    presetLoadedRef.current = true

    try {
      // Fetch the preset from assets folder
      const response = await fetch(`./assets/planner-presets/${presetFilename}`)
      if (!response.ok) {
        throw new Error(
          `Failed to fetch preset: ${response.status} ${response.statusText}`,
        )
      }

      const jsonData = await response.json()

      // Validate preset format
      if (!jsonData.products || !Array.isArray(jsonData.products)) {
        throw new Error("Invalid preset format: missing products array")
      }

      console.log(
        `[PLANNER] Importing preset with ${jsonData.products.length} products`,
      )

      // Clear current scene before importing
      resetScene()

      // Store scene settings to apply after materials are done
      const sceneSettings = jsonData.scene

      // Store the starting localId for reference
      const startingLocalId = nodeLocalIdsRef.current + 1

      // Add each product to the scene
      jsonData.products.forEach((product, index) => {
        const localId = startingLocalId + index

        // Set loading state
        setLoadingProducts((prev) => ({
          ...prev,
          [localId]: true,
        }))

        // Prepare the node for adding to the scene
        const node = {
          SKU: product.SKU,
          projectCode: product.projectCode,
          localId,
          parentLocalId: 1,
          draggable: true,
          tracked: true,
          position: product.position,
          rotationAxis: [0, 1, 0],
          rotationAngle: product.rotationAngle || 0,
          configurableMaterials: [],
          height: product.height || 1,
        }

        // Add to productNodesToAdd queue with proper refData
        setProductNodesToAdd((prev) => ({
          ...prev,
          [localId]: {
            ...node,
            height: product.height || 1,
            refData: {
              name: product.name || product.SKU,
              projectCode: product.projectCode,
              previewPath:
                product.previewPath ||
                `https://cdn.emersya.com/cl/3IB82Q24UW/p/${product.projectCode}/productNodes/86003?date=${Date.now()}`,
            },
            configurableMaterials: Array.isArray(product.configurableMaterials)
              ? product.configurableMaterials.map((mat) => ({
                  ...mat,
                  activeVariation: mat.appliedVariation,
                }))
              : [],
          },
        }))

        // Send message to add product to Emersya
        iframeRef.current.contentWindow.postMessage(
          {
            action: "addProductNodesInstances",
            id: localId,
            nodes: [node],
          },
          "*",
        )
      })

      // Update the nodeLocalIdsRef to prevent ID collisions
      nodeLocalIdsRef.current = startingLocalId + jsonData.products.length - 1

      // Collect all materials that need to be applied
      const materialsToApply = []
      jsonData.products.forEach((product, index) => {
        const localId = startingLocalId + index

        if (
          product.configurableMaterials &&
          Array.isArray(product.configurableMaterials)
        ) {
          product.configurableMaterials.forEach((material) => {
            if (material.name && material.appliedVariation) {
              materialsToApply.push({
                localId,
                configurableMaterial: material.name,
                materialVariation: material.appliedVariation,
              })
            }
          })
        }
      })

      // STEP 1: Apply floor material FIRST (3 seconds) to stop rotation
      if (sceneSettings?.floorMaterial) {
        setTimeout(() => {
          applyFloorMaterialOnly(sceneSettings.floorMaterial)
        }, 3000)
      }

      // STEP 2: Apply camera SECOND (4 seconds) to set the view
      if (sceneSettings?.camera) {
        setTimeout(() => {
          applyCameraOnly(sceneSettings.camera)
        }, 4000)
      }

      // STEP 3: Apply product materials LAST (8 seconds) with faster delays
      if (materialsToApply.length > 0) {
        console.log(
          `[PLANNER] Will apply ${materialsToApply.length} materials after floor/camera`,
        )

        setTimeout(() => {
          // Pass floor material to re-apply at the end as safety
          applyMaterialsFromPreset(
            materialsToApply,
            sceneSettings?.floorMaterial,
          )
        }, 8000)
      } else {
        // No materials to apply, just finish loading
        setTimeout(() => {
          setIsLoadingPreset(false)
        }, 5000)
      }
    } catch (error) {
      console.error("[PLANNER] Failed to load preset:", error)
      presetLoadedRef.current = false // Allow retry on error
      setIsLoadingPreset(false)
    }
  }

  /**
   * Apply only floor material (called early to stop rotation)
   * Calls PlannerPlayer's setFloorMaterialDirect function directly via ref
   * @param {string} floorMaterialName - Floor material name
   */
  const applyFloorMaterialOnly = (floorMaterialName) => {
    if (!floorMaterialName) return

    console.log(
      "[PLANNER] Applying floor material via PlannerPlayer ref:",
      floorMaterialName,
    )

    // Call the exposed function directly - uses EXACT same code as toggle button
    if (plannerPlayerRef.current?.setFloorMaterialDirect) {
      const success =
        plannerPlayerRef.current.setFloorMaterialDirect(floorMaterialName)
      if (!success) {
        console.warn("[PLANNER] Failed to apply floor material, will retry...")
        // Retry after a short delay if failed
        setTimeout(() => {
          plannerPlayerRef.current?.setFloorMaterialDirect(floorMaterialName)
        }, 1000)
      }
    } else {
      console.warn(
        "[PLANNER] PlannerPlayer ref not ready, will retry in 1 second",
      )
      setTimeout(() => {
        applyFloorMaterialOnly(floorMaterialName)
      }, 1000)
    }
  }

  /**
   * Apply only camera position (called early to stop rotation)
   * @param {Object} cameraData - Camera position data
   */
  const applyCameraOnly = (cameraData) => {
    if (!iframeRef.current?.contentWindow || !cameraData) return

    console.log("[PLANNER] Applying camera SECOND:", cameraData)

    iframeRef.current.contentWindow.postMessage(
      {
        action: "setCamera",
        position: cameraData.position,
        target: cameraData.target,
        up: cameraData.up || [0, 1, 0],
        fov: cameraData.fov || 45,
        transitionTime: 1000,
      },
      "*",
    )
  }

  /**
   * Apply materials from preset - Sequential with short delays (150ms for speed)
   * @param {Array} materialsToApply - Array of material info objects
   * @param {string} presetFloorMaterial - Floor material to re-apply at end (safety measure)
   */
  const applyMaterialsFromPreset = async (
    materialsToApply,
    presetFloorMaterial,
  ) => {
    console.log(
      `[PLANNER] Applying ${materialsToApply.length} product materials`,
    )

    for (let i = 0; i < materialsToApply.length; i++) {
      const { localId, configurableMaterial, materialVariation } =
        materialsToApply[i]

      // Update state
      setProductNodes((prev) => {
        const node = prev[localId]
        if (!node || !node.configurableMaterials) return prev

        const updatedMaterials = node.configurableMaterials.map((material) => {
          if (material.name === configurableMaterial) {
            return {
              ...material,
              appliedVariation: materialVariation,
              activeVariation: materialVariation,
            }
          }
          return material
        })

        return {
          ...prev,
          [localId]: {
            ...node,
            configurableMaterials: updatedMaterials,
          },
        }
      })

      // Send to Emersya - one material at a time
      try {
        iframeRef.current.contentWindow.postMessage(
          {
            action: "setMaterials",
            materials: [
              {
                configurableMaterial,
                materialVariation,
                nodeLocalIds: [localId],
              },
            ],
          },
          "*",
        )
      } catch (error) {
        console.error("[PLANNER] Error applying material:", error)
      }

      // Fast delay between materials (150ms for speed)
      if (i < materialsToApply.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 150))
      }
    }

    console.log(
      "[PLANNER] All preset materials applied, waiting for Emersya to finish processing...",
    )

    // Re-apply floor material at the end as safety
    // Emersya processes materials asynchronously - give it time to finish
    if (presetFloorMaterial) {
      await new Promise((resolve) => setTimeout(resolve, 4000))
      console.log("[PLANNER] Applying floor material:", presetFloorMaterial)
      plannerPlayerRef.current?.setFloorMaterialDirect(presetFloorMaterial)
    }

    setIsLoadingPreset(false)
  }

  /**
   * Apply only scene settings (floor material and camera) without materials
   * Used when preset has no product materials but has scene settings
   * @param {Object} sceneSettings - Scene settings (floor, camera)
   */
  const applySceneSettingsOnly = async (sceneSettings) => {
    console.log("[PLANNER] Applying scene settings only (no product materials)")

    if (!sceneSettings) {
      setIsLoadingPreset(false)
      return
    }

    // Apply floor material using the PlannerPlayer ref
    if (sceneSettings.floorMaterial) {
      console.log(
        "[PLANNER] Floor material applied:",
        sceneSettings.floorMaterial,
      )
      plannerPlayerRef.current?.setFloorMaterialDirect(
        sceneSettings.floorMaterial,
      )
    }

    // Wait before camera
    await new Promise((resolve) => setTimeout(resolve, 500))

    // Apply camera position
    if (sceneSettings.camera && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        {
          action: "setCamera",
          position: sceneSettings.camera.position,
          target: sceneSettings.camera.target,
          up: sceneSettings.camera.up || [0, 1, 0],
          fov: sceneSettings.camera.fov || 45,
          transitionTime: 1500,
        },
        "*",
      )
      console.log("[PLANNER] Camera position applied:", sceneSettings.camera)
    }

    setIsLoadingPreset(false)
  }

  /* ----------------------------------------------------------------
    1) Setup: listeners for clicks outside dropdown, empty image, etc.
  -----------------------------------------------------------------*/

  // Transparent image (ghost) for drag
  useEffect(() => {
    const emptyImage = new Image()
    emptyImage.src =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH4QYHDTQ15hoLGQAAAB1pVFh0Q29tbWVudAAAAAAAQ3JlYXRlZCB3aXRoIEdJTVBkLmUHAAAAC0lEQVQI12NgAAIAAAUAAeImBZsAAAAASUVORK5CYII="
    emptyImageRef.current = emptyImage
  }, [])

  // Check cache on mount and load data if available
  useEffect(() => {
    loadCachedData()
  }, [])

  // Load data from cache if available
  const loadCachedData = () => {
    try {
      const lastFetch = localStorage.getItem(LS_KEYS.LAST_FETCH)
      const now = new Date().getTime()

      // Check if cache is still valid (within 24 hours)
      if (lastFetch && now - parseInt(lastFetch) < CACHE_TIMEOUT) {
        const cachedCategories = JSON.parse(
          localStorage.getItem(LS_KEYS.CATEGORIES),
        )
        const cachedProductsList = JSON.parse(
          localStorage.getItem(LS_KEYS.PRODUCTS_LIST),
        )

        if (cachedCategories && cachedProductsList) {
          console.log("[CACHE] Loading data from cache")
          console.log("[getPlannerUsableProjects] products:", cachedProductsList)
          setCategories(cachedCategories)
          setProductsList(cachedProductsList)
          resizeFloor()
          setLoading(false)
          return true
        }
      }
    } catch (error) {
      console.error("[CACHE ERROR]", error)
      // Clear any corrupted cache
      clearCache()
    }
    return false
  }

  // Save data to cache
  const saveToCache = (categoriesData, productsListData) => {
    try {
      localStorage.setItem(LS_KEYS.CATEGORIES, JSON.stringify(categoriesData))
      localStorage.setItem(
        LS_KEYS.PRODUCTS_LIST,
        JSON.stringify(productsListData),
      )
      localStorage.setItem(LS_KEYS.LAST_FETCH, new Date().getTime().toString())
      console.log("[CACHE] Data saved to cache")
    } catch (error) {
      console.error("[CACHE ERROR]", error)
    }
  }

  // Initialize products and materials from API
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Load all products from API (for family/product matching)
        const productsData = await fetchProducts()
        setAllProducts(productsData)

        // Load material links
        const links = await fetchMaterialLinks()
        setMaterialLinks(links)

        // Initialize material manager
        materialManager.initializeAllMaterials()

        console.log(
          "[PLANNER] Initialized with",
          productsData.length,
          "products from API",
        )
      } catch (error) {
        console.error("[PLANNER] Failed to initialize:", error)
      }
    }

    initializeData()
  }, [])

  // Clear cache
  const clearCache = () => {
    try {
      localStorage.removeItem(LS_KEYS.CATEGORIES)
      localStorage.removeItem(LS_KEYS.PRODUCTS_LIST)
      localStorage.removeItem(LS_KEYS.LAST_FETCH)
    } catch (error) {
      console.error("[CACHE ERROR]", error)
    }
  }

  // Iframe message listener
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data

      if (!data) return

      switch (data.action) {
        case "onError":
          handleError(data)
          break
        case "onSuccess":
          handleSuccess(data)
          break
        case "onProductNodeInstanceConfigurableMaterialsGet":
          handleConfigurableMaterials(data)
          break
        case "onCollisionsUpdate":
          handleCollisionsUpdate(data)
          break
        case "onMeshSelection":
          handleMeshSelection(data)
          break
        case "onStateChange":
          handleStateChange(data)
          break
        case "onProductNodeInstanceDraggingUpdate":
          if (productNodes[data.nodeLocalId]) {
            const pn = productNodes[data.nodeLocalId]
            // console.log(pn);
            pn.position = data.position
          }
          if (productNodesToAdd[data.nodeLocalId]) {
            const pn = productNodesToAdd[data.nodeLocalId]
            pn.position = data.position
          }
          break

        case "onProductNodeInstanceDraggingStopped":
          // The stop event always follows an update event
          // so we don't need to update position here
          break
        default:
          // console.log("Unknown action:", data);
          break
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [productNodes, productNodesToAdd])

  // Listener for Emersya initialization
  useEffect(() => {
    const handleEmersyaInit = () => {
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        console.log("Emersya viewer initialized and available")
        setEmersyaViewerInstance(window.emViewers["emersyaIframe"])
      }
    }

    // Check if already initialized
    if (window.emViewers && window.emViewers["emersyaIframe"]) {
      handleEmersyaInit()
    }

    // Listen for initialization
    document.addEventListener("emersyaViewerInitialized", handleEmersyaInit)

    return () => {
      document.removeEventListener(
        "emersyaViewerInitialized",
        handleEmersyaInit,
      )
    }
  }, [])

  // Check for URL preset parameter and load when viewer is ready
  useEffect(() => {
    const presetFilename = getPresetFromUrl()
    if (!presetFilename || presetLoadedRef.current || loading) return

    // Wait for viewer to be ready before loading preset
    console.log(`[PLANNER] Preset parameter detected: ${presetFilename}`)

    // Small delay to ensure viewer is fully ready
    const timer = setTimeout(() => {
      if (iframeRef.current && !loading) {
        loadPresetFromUrl(presetFilename)
      }
    }, 2000)

    return () => clearTimeout(timer)
  }, [loading])

  // On iframe load, register as callback
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(
        { action: "registerCallback" },
        "*",
      )
    }
  }

  /* ----------------------------------------------------------------
     2) HANDLER for errors and successes from viewer
  -----------------------------------------------------------------*/
  const handleError = (data) => {
    const { callAction, id, message } = data
    console.error(`[EMERSYA ERROR] in ${callAction}: ${message}`)

    // If adding the productNode fails, remove it from the "toAdd" queue
    if (
      callAction === "startProductNodeDrop" ||
      callAction === "addProductNodeInstance" ||
      callAction === "addProductNodesInstances"
    ) {
      setProductNodesToAdd((prev) => {
        const updated = { ...prev }
        delete updated[id]
        return updated
      })

      // Remove loading state
      setLoadingProducts((prev) => {
        const updated = { ...prev }
        delete updated[id]
        return updated
      })
    }
  }

  const [openGroups, setOpenGroups] = useState({})

  const duplicateProductNode = (pn) => (event) => {
    event.stopPropagation()
    event.preventDefault()

    const localId = nodeLocalIdsRef.current + 1
    nodeLocalIdsRef.current += 1

    // Get count of existing products with same projectCode for offset calculation
    const existingCount = Object.values(productNodes).filter(
      (p) => p.projectCode === pn.projectCode,
    ).length

    // Create spiral-like placement for duplicates
    const angle = (existingCount * Math.PI) / 4 // 45-degree increments
    const radius = 0.5 // Base radius
    const offsetPosition = [...pn.position]
    offsetPosition[0] += Math.cos(angle) * radius // X offset
    offsetPosition[2] += Math.sin(angle) * radius // Z offset

    // Set loading state
    setLoadingProducts((prev) => ({
      ...prev,
      [localId]: true,
    }))

    const nodes = [
      {
        SKU: pn.SKU,
        projectCode: pn.projectCode,
        localId,
        parentLocalId: 1,
        draggable: true,
        tracked: true,
        position: offsetPosition,
        rotationAxis: [0, 1, 0],
        rotationAngle: pn.rotationAngle || 0,
        configurableMaterials: [],
      },
    ]

    iframeRef.current.contentWindow.postMessage(
      {
        action: "addProductNodesInstances",
        id: localId,
        nodes,
      },
      "*",
    )

    setProductNodesToAdd((prev) => ({
      ...prev,
      [localId]: {
        SKU: pn.SKU,
        projectCode: pn.projectCode,
        localId,
        parentLocalId: 1,
        refData: pn.refData,
        position: offsetPosition,
        rotationAxis: [0, 1, 0],
        rotationAngle: pn.rotationAngle || 0,
        height: pn.height || 1,
      },
    }))
  }

  const handleSuccess = (data) => {
    const { callAction, id, data: responseData } = data
    switch (callAction) {
      case "startProductNodeDrop":
      case "addProductNodeInstance":
      case "addProductNodesInstances":
        if (productNodesToAdd[id]) {
          const pn = productNodesToAdd[id]
          console.log(
            "[SUCCESS] Added productNode with localId =",
            pn.localId,
            " => projectCode =",
            pn.projectCode,
          )
          console.log(data)
          // Add to scene
          setProductNodes((prev) => ({
            ...prev,
            [pn.localId]: {
              ...pn,
              position: [pn.position[0], pn.position[1] + 0.01, pn.position[2]],
            },
          }))

          // Remove from toAdd queue
          setProductNodesToAdd((prev) => {
            const updated = { ...prev }
            delete updated[id]
            return updated
          })

          // Remove loading state
          setLoadingProducts((prev) => {
            const updated = { ...prev }
            delete updated[id]
            return updated
          })

          // Request configurable materials
          iframeRef.current.contentWindow.postMessage(
            {
              action: "getProductNodeInstanceConfigurableMaterials",
              localId: pn.localId,
            },
            "*",
          )

          // Do direct matching using projectCode
          matchProductWithLocal(pn.localId, pn.projectCode)
        }
        break
      case "getPlannerUsableProjects":
        // Check if we already have data in cache and state
        if (categories.length > 0 && productsList.length > 0) {
          console.log("[CACHE] Using cached data instead of API response")

          break
        }
        setCategories(responseData)
        if (Array.isArray(responseData)) {
          // Extract all productNodes
          const allProducts = []
          responseData.forEach((category) => {
            category.projects.forEach((project) => {
              project.productNodes.forEach((productNode) => {
                allProducts.push({
                  ...productNode,
                  projectCode: project.code,
                  category: category.name,
                })
              })
            })
          })

          setProductsList(allProducts)

          console.log("[getPlannerUsableProjects] TOT =", allProducts.length)
          console.log("[getPlannerUsableProjects] products:", allProducts)

          // Cache the data
          saveToCache(responseData, allProducts)
        }
        resizeFloor()
        setLoading(false)
        break

      case "removeProductNodeInstance":
        setProductNodes((prev) => {
          const updated = { ...prev }
          delete updated[data.id]
          return updated
        })
        break

      case "resizePlannerSpace":
        // No specific action
        break

      case "setMaterials":
        // Material applied successfully (responseData is often undefined — normal)
        break

      case "selectProductNode":
        // Product selected in viewer (bounding box shown) — no action needed
        break

      default:
        console.log("[EMERSYA] Unhandled success action:", callAction)
    }
  }

  // Function to fetch similar to what you did in FamilyAccordion
  // to retrieve the p/XXXXX/presets string from HTML and extract XXXXX
  const matchProductWithLocal = (localId, projectCodeFromViewer) => {
    console.log(
      `[matchProductWithLocal] localId=${localId}, projectCodeFromViewer=${projectCodeFromViewer}`,
    )

    // Try to find a matching product in products.js using projectCode
    const matched = products.find(
      (p) => p.projectCode === projectCodeFromViewer,
    )

    // If not in local products.js, try to find showcase code from API data
    const apiMatch = !matched
      ? allProducts.find((p) => p.projectCode === projectCodeFromViewer)
      : null

    // Update productNodes state with the matched product (or null if no match)
    setProductNodes((prev) => {
      const prevPN = prev[localId]
      if (!prevPN) return prev

      // Make sure configurableMaterials is always an array
      const configurableMaterials = prevPN.configurableMaterials || []

      // Ensure showcase code is always available in refData for preview fetching
      const existingCode = prevPN.refData?.code
      const showcaseCode = existingCode || matched?.code || apiMatch?.code

      return {
        ...prev,
        [localId]: {
          ...prevPN,
          matchedLocalProduct: matched || null,
          // Make sure refData contains the previewPath, code and price
          refData: {
            ...prevPN.refData,
            code: showcaseCode || prevPN.refData?.code,
            price:
              prevPN.refData?.price ||
              matched?.price ||
              apiMatch?.price ||
              null,
            previewPath:
              prevPN.refData?.previewPath ||
              `https://cdn.emersya.com/cl/3IB82Q24UW/p/${projectCodeFromViewer}/productNodes/86003`,
            name: matched
              ? matched.name
              : apiMatch?.name || prevPN.refData?.name || "Prodotto",
          },
          configurableMaterials,
        },
      }
    })

    console.log(
      matched
        ? `[MATCH] Found match with products.js => ${matched.name}`
        : `[NO MATCH] No match in products.js for projectCode=${projectCodeFromViewer}`,
    )

    // Request configurable materials if not already requested
    // Add longer delay to ensure materials are fully loaded
    if (matched) {
      setTimeout(() => {
        iframeRef.current.contentWindow.postMessage(
          {
            action: "getProductNodeInstanceConfigurableMaterials",
            localId: localId,
          },
          "*",
        )
      }, 800) // Increased from 500 to 800ms for better material loading
    }
  }

  /* ----------------------------------------------------------------
     3) SPECIALIZED HANDLERS for collisions, materials, meshSelection...
  -----------------------------------------------------------------*/
  // Configurable materials from viewer
  const handleConfigurableMaterials = async (data) => {
    const { localId, configurableMaterials } = data
    console.log(
      `[handleConfigurableMaterials] localId=${localId}, => `,
      configurableMaterials,
    )

    // Get the product node to find its family
    const productNode = productNodes[localId] || productNodesToAdd[localId]
    if (!productNode) {
      console.warn(
        `[handleConfigurableMaterials] Product node not found for localId=${localId}`,
      )
      return
    }

    // Find the product in allProducts to get family and material settings
    const apiProduct = allProducts.find(
      (p) => p.code === productNode.refData?.code,
    )
    const familyName = apiProduct?.family || selectedFamily
    const productCode = apiProduct?.code

    console.log(
      `[handleConfigurableMaterials] Family: ${familyName}, Product: ${productCode}`,
    )

    // Set family settings in materialManager if available
    if (apiProduct && apiProduct.familyDefaults) {
      const familyDefaults = apiProduct.familyDefaults?.defaultMaterials || {}
      const familyTypeLinks = apiProduct.familyDefaults?.typeLinks || []
      const familyCollectionTranslations =
        apiProduct.familyDefaults?.collectionTranslations || {}

      // Ensure each material type has both standard and optional arrays
      Object.keys(familyDefaults).forEach((type) => {
        if (!familyDefaults[type].standard) familyDefaults[type].standard = []
        if (!familyDefaults[type].optional) familyDefaults[type].optional = []
      })

      materialManager.setFamilySettings(familyName, {
        types: familyDefaults,
        typeLinks: familyTypeLinks,
        collectionTranslations: familyCollectionTranslations,
      })
    }

    // Set product-specific settings if available
    if (
      apiProduct &&
      apiProduct.materials &&
      Object.keys(apiProduct.materials).length > 0
    ) {
      const productMaterials = { ...apiProduct.materials }
      Object.keys(productMaterials).forEach((type) => {
        if (!productMaterials[type].standard)
          productMaterials[type].standard = []
        if (!productMaterials[type].optional)
          productMaterials[type].optional = []
      })

      materialManager.setProductSettings(productCode, {
        types: productMaterials,
        typeLinks: apiProduct.typeLinks || [],
      })
    }

    // Initialize materialManager with the viewer materials
    materialManager.initialize(configurableMaterials, familyName, productCode)

    // Get available materials with family/product settings applied
    const availableMaterials = materialManager.getAvailableMaterials(
      familyName,
      productCode,
    )

    console.log(
      "[handleConfigurableMaterials] Available materials for product:",
      availableMaterials,
    )

    // Add default appliedVariation to enriched materials
    const enrichedWithDefaults = availableMaterials.map((material) => {
      // Find the default variation from the raw configurableMaterials
      const rawMaterial = configurableMaterials.find(
        (m) => m.name === material.technicalName,
      )

      const appliedVariation =
        rawMaterial?.activeVariation ||
        (material.variations && material.variations.length > 0
          ? material.variations[0].name
          : null)

      return {
        ...material,
        name: material.technicalName, // ✅ ADD: ProductItem expects 'name'
        materialVariations: material.variations, // ✅ ADD: ProductItem expects 'materialVariations'
        appliedVariation,
        activeVariation: appliedVariation,
      }
    })

    // ✅ Store the enriched materials with thumbnails
    setProductNodes((prev) => {
      const node = prev[localId]
      if (!node) return prev

      return {
        ...prev,
        [localId]: {
          ...node,
          configurableMaterials: enrichedWithDefaults,
          familyName,
          productCode,
        },
      }
    })
  }
  // Collisions
  const handleCollisionsUpdate = (data) => {
    const { localIds } = data
    // localIds => the colliding productNodes
    setProductNodes((prev) => {
      const updated = { ...prev }
      Object.keys(updated).forEach((localId) => {
        if (localIds.includes(parseInt(localId))) {
          updated[localId].colliding = true
        } else {
          updated[localId].colliding = false
        }
      })
      return updated
    })

    // update ghost
    const nodesToUpdate = Object.keys(productNodes).map((localId) => ({
      localId: parseInt(localId),
      renderAsGhost: productNodes[localId].colliding,
    }))

    iframeRef.current.contentWindow.postMessage(
      {
        action: "setProductNodesToGhostRenderingMode",
        nodes: nodesToUpdate,
      },
      "*",
    )
  }

  // Mesh selection — only track in sidebar, don't send selectProductNode back (avoids bounding box)
  const handleMeshSelection = (data) => {
    const { nodeLocalId, position } = data
    if (productNodes[nodeLocalId]) {
      setProductNodes((prev) => ({
        ...prev,
        [nodeLocalId]: {
          ...prev[nodeLocalId],
          currentHeightFromFloor: position?.[1] || 0,
        },
      }))
      setSelectedProductLocalId(nodeLocalId)
    }
  }

  // State handling
  const handleStateChange = (data) => {
    const viewerState = data.state?.viewerState
    if (viewerState === "loaded") {
      console.log(
        "[onStateChange] => viewerState=loaded => fetch getPlannerUsableProjects",
      )

      // Cache disabled — always fetch fresh data from Emersya
      // const loadedFromCache = loadCachedData()
      clearCache()
      iframeRef.current.contentWindow.postMessage(
        {
          action: "getPlannerUsableProjects",
        },
        "*",
      )

      // Try to get and store the Emersya viewer instance
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        console.log("Emersya viewer detected on state change")
        setEmersyaViewerInstance(window.emViewers["emersyaIframe"])
      }
    }
  }

  /* ----------------------------------------------------------------
     4) UTILITY FUNCTIONS: resize floor, drag, click, removal, rotation...
  -----------------------------------------------------------------*/
  // Resizing floor
  const resizeFloor = () => {
    const { length, width, height } = floorDimensions
    iframeRef.current.contentWindow.postMessage(
      {
        action: "resizePlannerSpace",
        length: parseFloat(length),
        width: parseFloat(width),
        height: parseFloat(height),
      },
      "*",
    )
  }

  // Dragging a product (from left)
  const onDragStart = (productNode) => (event) => {
    setIgnoreClick(true)
    setIsProductDropdownOpen(false)

    const localId = nodeLocalIdsRef.current + 1
    nodeLocalIdsRef.current += 1

    console.log("[onDragStart] About to add localId=", localId, productNode)

    // Set loading state
    setLoadingProducts((prev) => ({
      ...prev,
      [localId]: true,
    }))

    const nodesToAdd = [
      {
        SKU: productNode.SKU,
        projectCode: productNode.projectCode,
        localId,
        parentLocalId: 1,
        position: [0, -0.001, 0],
        rotationAxis: [0, 1, 0],
        rotationAngle: 0,
      },
    ]

    // "startProductNodeDrop" => Emersya will add it with drag
    iframeRef.current.contentWindow.postMessage(
      {
        action: "startProductNodeDrop",
        id: localId,
        nodesToAdd,
      },
      "*",
    )

    setProductNodesToAdd((prev) => ({
      ...prev,
      [localId]: {
        SKU: productNode.SKU,
        projectCode: productNode.projectCode,
        localId,
        parentLocalId: 1,
        refData: {
          ...productNode,
          name: productNode.name,
          previewPath:
            productNode.previewPath ||
            `https://cdn.emersya.com/cl/3IB82Q24UW/p/${productNode.projectCode}/productNodes/86003`,
        },
        position: [0, 0.1, 0],
        rotationAngle: 0,
        configurableMaterials: [],
        height: 1,
      },
    }))

    // Transparent image
    event.dataTransfer.setDragImage(emptyImageRef.current, 0, 0)
    event.preventDefault()
    event.stopPropagation()
  }

  // Click on a product => immediate add
  const onClick = (productNode) => (event) => {
    if (ignoreClick) {
      setIgnoreClick(false)
      return
    }
    event.preventDefault()
    event.stopPropagation()

    const localId = nodeLocalIdsRef.current + 1
    nodeLocalIdsRef.current += 1

    console.log("[onClick] Adding localId=", localId, productNode)

    // Set loading state
    setLoadingProducts((prev) => ({
      ...prev,
      [localId]: true,
    }))

    const nodes = [
      {
        SKU: productNode.SKU,
        projectCode: productNode.projectCode,
        localId,
        parentLocalId: 1,
        draggable: true,
        tracked: true,
        position: [0, 0.1, 0],
        rotationAxis: [0, 1, 0],
        rotationAngle: 0,
        configurableMaterials: [],
      },
    ]

    iframeRef.current.contentWindow.postMessage(
      {
        action: "addProductNodesInstances",
        id: localId,
        nodes,
      },
      "*",
    )

    setProductNodesToAdd((prev) => ({
      ...prev,
      [localId]: {
        SKU: productNode.SKU,
        projectCode: productNode.projectCode,
        localId,
        parentLocalId: 1,
        refData: {
          ...productNode,
          name: productNode.name,
          previewPath:
            productNode.previewPath ||
            `https://cdn.emersya.com/cl/3IB82Q24UW/p/${productNode.projectCode}/productNodes/86003`,
        },
        position: [0, 0, 0],
        rotationAxis: [0, 1, 0],
        rotationAngle: 0,
        height: 1,
      },
    }))
  }

  // Remove product
  const removeProductNodeInstance = (pn) => (event) => {
    event.stopPropagation()
    event.preventDefault()
    console.log("[removeProductNodeInstance] => localId=", pn.localId)
    iframeRef.current.contentWindow.postMessage(
      {
        action: "removeProductNodeInstance",
        localId: pn.localId,
        id: pn.localId,
      },
      "*",
    )
  }

  // Rotate product
  const rotateProductNodeInstance = (pn) => (event) => {
    event.stopPropagation()
    event.preventDefault()
    const angleDeg = parseInt(event.target.value)
    const angleRad = (angleDeg * Math.PI) / 180

    setProductNodes((prev) => ({
      ...prev,
      [pn.localId]: {
        ...prev[pn.localId],
        rotationAngle: angleRad,
      },
    }))

    iframeRef.current.contentWindow.postMessage(
      {
        action: "setProductNodesInstancesTransformations",
        nodes: [
          {
            localId: pn.localId,
            rotationAxis: [0, 1, 0],
            rotationAngle: angleRad,
          },
        ],
      },
      "*",
    )
  }

  // Product height
  const changeProductNodeInstanceHeight = (pn) => (event) => {
    event.stopPropagation()
    event.preventDefault()

    const oldHeight = pn.height || 1
    const newHeight = parseFloat(event.target.value) / 100

    // Important: Keep the existing position and only update Y
    const currentPosition = pn.position || [0, 0, 0]
    const newPosition = [
      currentPosition[0],
      currentPosition[1] + (newHeight - oldHeight),
      currentPosition[2],
    ]

    iframeRef.current.contentWindow.postMessage(
      {
        action: "setProductNodesInstancesTransformations",
        nodes: [
          {
            localId: pn.localId,
            position: newPosition,
            height: newHeight,
          },
        ],
      },
      "*",
    )

    // Update state with both new height and position
    setProductNodes((prev) => ({
      ...prev,
      [pn.localId]: {
        ...prev[pn.localId],
        height: newHeight,
        position: newPosition,
      },
    }))
  }

  // Select product
  const selectProductNodeInstance = (pn) => () => {
    iframeRef.current.contentWindow.postMessage(
      {
        action: "selectProductNode",
        localId: pn.localId,
      },
      "*",
    )
  }

  // Apply material => Message to Emersya
  const applyMaterial = (pn, configurableMaterial, materialVariation) => {
    console.log("[applyMaterial]", {
      localId: pn.localId,
      technicalName: configurableMaterial,
      material: materialVariation,
    })

    // Get the variation name - handle both string and object
    const variationName =
      typeof materialVariation === "string"
        ? materialVariation
        : materialVariation.viewerName || materialVariation.name

    console.log("[applyMaterial] Using variation name:", variationName)

    // First update the state to ensure our UI reflects the change immediately
    setProductNodes((prev) => {
      const node = prev[pn.localId]
      if (!node) return prev

      // If the node has configurableMaterials, update the applied variation
      if (node.configurableMaterials) {
        const updatedMaterials = node.configurableMaterials.map((material) => {
          if (material.technicalName === configurableMaterial) {
            return {
              ...material,
              appliedVariation: variationName,
              activeVariation: variationName,
            }
          }
          return material
        })

        return {
          ...prev,
          [pn.localId]: {
            ...node,
            configurableMaterials: updatedMaterials,
          },
        }
      }

      return prev
    })

    // Then send the message to Emersya to apply the material
    setTimeout(() => {
      try {
        // ✅ FIX: Use correct iframe reference
        if (iframeRef.current && iframeRef.current.contentWindow) {
          console.log("[applyMaterial] Sending to Emersya:", {
            material: variationName,
            configurableMaterial: configurableMaterial,
          })

          iframeRef.current.contentWindow.postMessage(
            {
              action: "setMaterials",
              materials: [
                {
                  materialVariation: variationName,
                  configurableMaterial: configurableMaterial,
                },
              ],
              nodeLocalIds: [pn.localId],
            },
            "*",
          )
        } else {
          console.error("[applyMaterial] iframe not available")
        }
      } catch (error) {
        console.error("[applyMaterial] Error:", error)
      }
    }, 50)
  }

  /* ----------------------------------------------------------------
     5) SHIFT => snapping
  -----------------------------------------------------------------*/
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Shift") {
        iframeRef.current?.contentWindow.postMessage(
          { action: "togglePlannerSnapMode", value: true },
          "*",
        )
      }
    }
    const handleKeyUp = (event) => {
      if (event.key === "Shift") {
        iframeRef.current?.contentWindow.postMessage(
          { action: "togglePlannerSnapMode", value: false },
          "*",
        )
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
    }
  }, [])

  /* ----------------------------------------------------------------
     6) UI
  -----------------------------------------------------------------*/

  // Removed - using setIsSidebarOpen directly like App.js

  /**
   * Wrapper function for "applyMaterial", so we pass it to the accordion component with the signature we prefer:
   *   applyMaterial(pn, "imbottito" | "struttura" | etc, "VARIANT NAME")
   */
  function applyMaterialFromAccordion(pn, configurableMat, variationName) {
    applyMaterial(pn, configurableMat, variationName)
  }

  /**
   * Handle category change - updates selected category for filtering
   */
  const handleCategoryChange = (newCategory) => {
    console.log(`[PLANNER] Selected category: ${newCategory}`)
    setSelectedCategory(newCategory)
    // Reset family when category changes
    setSelectedFamily(null)
  }

  /**
   * Handle family change - updates selected family for filtering
   */
  const handleFamilyChange = (newFamily) => {
    console.log(`[PLANNER] Selected family: ${newFamily}`)
    setSelectedFamily(newFamily)
  }

  /**
   * Handle drag start - for dragging products into the planner
   * Uses startProductNodeDrop to show ghost and drop at position
   */
  /* ----------------------------------------------------------------
     3) DRAG & DROP HANDLER (Mirrors planner_doc_example.html)
  -----------------------------------------------------------------*/
  const handleDragStart = (productFromAccordion, event) => {
    setIgnoreClick(true)
    //setIsProductDropdownOpen(false); // Close dropdown if open

    if (!productFromAccordion) return

    // Find the REAL planner product using projectCode/SKU if needed,
    // or just use the passed object if it has what we need.
    // Ideally productFromAccordion has .projectCode.
    const basePlannerProduct =
      productsList.find(
        (p) => p.projectCode === productFromAccordion.projectCode,
      ) || productFromAccordion

    // Merge: keep code and previewPath from accordion product (enrichment data)
    const plannerProduct = {
      ...basePlannerProduct,
      code: productFromAccordion.code || basePlannerProduct.code,
      name: productFromAccordion.name || basePlannerProduct.name,
      previewPath:
        productFromAccordion.previewPath || basePlannerProduct.previewPath,
    }

    const localId = nodeLocalIdsRef.current + 1
    nodeLocalIdsRef.current += 1

    console.log("[handleDragStart] Adding localId=", localId, plannerProduct)

    // Prepare node structure (matches vanilla example exactly)
    const nodeData = {
      SKU: plannerProduct.SKU || plannerProduct.name,
      projectCode: plannerProduct.projectCode,
      localId: localId,
      parentLocalId: 1,
      position: [0, 0, 0],
      rotationAxis: [0, 1, 0],
      rotationAngle: 0,
    }

    const nodesToAdd = [nodeData]

    // "startProductNodeDrop" => Emersya will add it with drag
    // Must happen BEFORE setDragImage (matching vanilla example order)
    iframeRef.current?.contentWindow?.postMessage(
      {
        action: "startProductNodeDrop",
        id: localId,
        nodesToAdd,
      },
      "*",
    )

    // Store product data for tracking (matching vanilla example)
    const newProductState = {
      ...nodeData,
      refData: {
        ...plannerProduct,
        name: plannerProduct.name,
        previewPath:
          plannerProduct.previewPath || plannerProduct.refData?.previewPath,
      },
      configurableMaterials: [],
      height: 1,
    }

    setProductNodesToAdd((prev) => ({
      ...prev,
      [localId]: newProductState,
    }))

    setProductNodes((prev) => ({
      ...prev,
      [localId]: newProductState,
    }))

    // Set empty drag image AFTER postMessage (vanilla example order)
    if (emptyImageRef.current) {
      event.dataTransfer.setDragImage(emptyImageRef.current, 0, 0)
    }

    // Matching vanilla example: preventDefault + stopPropagation at end
    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * Add product to planner scene
   * Based on Emersya demo onClick function
   */
  const handleClick = (plannerProduct) => {
    if (!plannerProduct) return

    // Generate unique local ID for this instance
    const newLocalId = ++nodeLocalIdsRef.current

    // Set loading state
    setLoadingProducts((prev) => ({
      ...prev,
      [newLocalId]: true,
    }))

    const nodesToAdd = [
      {
        SKU: plannerProduct.SKU,
        projectCode: plannerProduct.projectCode,
        localId: newLocalId,
        parentLocalId: 1,
        draggable: true,
        tracked: true,
        position: [0, 0.1, 0],
        rotationAxis: [0, 1, 0],
        rotationAngle: 0,
        configurableMaterials: [],
      },
    ]

    // Send to Emersya viewer
    iframeRef.current?.contentWindow?.postMessage(
      {
        action: "addProductNodesInstances",
        id: newLocalId,
        nodes: nodesToAdd,
      },
      "*",
    )

    // Store in productNodesToAdd for tracking
    setProductNodesToAdd((prev) => ({
      ...prev,
      [newLocalId]: {
        SKU: plannerProduct.SKU,
        projectCode: plannerProduct.projectCode,
        localId: newLocalId,
        parentLocalId: 1,
        refData: plannerProduct,
        position: [0, 0, 0],
        rotationAxis: [0, 1, 0],
        rotationAngle: 0,
        height: 1,
      },
    }))

    console.log(`[PLANNER] Added product to scene:`, {
      SKU: plannerProduct.SKU,
      projectCode: plannerProduct.projectCode,
      localId: newLocalId,
    })
  }

  /**
   * Handle product selection from FamilyAccordion - adds product to scene
   */
  const handleProductToAdd = (productCode, previewPath) => {
    if (!productCode) return

    console.log(`[PLANNER] Adding product with code: ${productCode}`)

    // Find the product in allProducts (API data) using the code
    const apiProduct = allProducts.find((p) => p.code === productCode)

    if (!apiProduct || !apiProduct.projectCode) {
      console.warn(
        `[PLANNER] Product ${productCode} not found or missing projectCode`,
      )
      return
    }

    // Find the corresponding planner product using projectCode
    const plannerProduct = productsList.find(
      (p) => p.projectCode === apiProduct.projectCode,
    )

    if (!plannerProduct) {
      console.warn(
        `[PLANNER] Planner product not found for projectCode: ${apiProduct.projectCode}`,
      )
      return
    }

    console.log(`[PLANNER] Adding product:`, {
      name: apiProduct.name,
      code: apiProduct.code,
      projectCode: apiProduct.projectCode,
    })

    // Merge API data into planner product so refData has name, code, price and previewPath
    const enrichedProduct = {
      ...plannerProduct,
      name: apiProduct.name || plannerProduct.SKU,
      code: apiProduct.code,
      price: apiProduct.price || null,
      previewPath: previewPath || null,
    }

    // Add product to scene (simulating click)
    handleClick(enrichedProduct)
  }

  /**
   * Get ordered and filtered materials for display
   * Same logic as App.js getOrderedMaterials
   */
  const getOrderedMaterialsForProduct = (viewerMaterials, familyName) => {
    if (!viewerMaterials || viewerMaterials.length === 0) return []

    const familySettings = materialManager.familySettings[familyName]
    const defaultMaterials = familySettings?.types || {}

    // Get the order from defaultMaterials keys
    const defaultOrder = Object.keys(defaultMaterials)

    // Filter materials that have enough variations (≥2) AND are not hidden
    const filteredMaterials = viewerMaterials.filter((mat) => {
      const hasEnoughVariations = mat.variations.length >= 2

      // Check if this material type is hidden in defaultMaterials
      const materialConfig = defaultMaterials[mat.technicalName]
      const isHidden = materialConfig?.hidden === true

      return hasEnoughVariations && !isHidden
    })

    // Sort materials according to defaultMaterials order
    const orderedMaterials = filteredMaterials.sort((a, b) => {
      const aIndex = defaultOrder.indexOf(a.technicalName)
      const bIndex = defaultOrder.indexOf(b.technicalName)

      // If both are in defaultMaterials, sort by their order
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex

      // If only a is in defaultMaterials, a comes first
      if (aIndex !== -1 && bIndex === -1) return -1

      // If only b is in defaultMaterials, b comes first
      if (aIndex === -1 && bIndex !== -1) return 1

      // If neither is in defaultMaterials, maintain original order
      return 0
    })

    return orderedMaterials
  }

  /* ----------------------------------------------------------------
     7) RENDER
  -----------------------------------------------------------------*/
  return (
    <>
      {loading && (
        <div
          className="ay-w-full ay-h-screen ay-bg-white ay-opacity-95 ay-fixed ay-top-0 ay-left-0 ay-z-50 ay-flex ay-justify-center ay-items-center ay-text-3xl"
          id="loader"
        >
          <img
            src={logo}
            alt="Logo"
            className="ay-animate-scale-pulse ay-max-w-[70%] md:ay-max-w-[250px]"
          />
        </div>
      )}

      <div className="ay-relative ay-h-screen ay-overflow-hidden">
        {/* IFRAME - Full screen */}
        <div
          className="ay-w-full ay-h-screen ay-relative"
        >
          {/* Logo in top left corner */}
          <a href="https://presentazione.ayea.it">
            <img
              src={logoNu}
              alt="Logo"
              className="ay-absolute ay-top-4 ay-left-4 ay-z-10 ay-h-8 md:ay-h-10 ay-w-auto"
            />
          </a>
          <PlannerPlayer
            ref={plannerPlayerRef}
            iframeRef={iframeRef}
            onLoad={handleIframeLoad}
            floorMaterial={floorMaterial}
            setFloorMaterial={setFloorMaterial}
          />

          {/* Loading Indicators */}
          {(Object.keys(loadingProducts).length > 0 || isLoadingPreset) && (
            <div className="ay-absolute ay-top-2 ay-left-1/2 -ay-translate-x-1/2 ay-flex ay-items-center ay-bg-white ay-bg-opacity-80 ay-px-3 ay-py-2 ay-rounded-lg ay-shadow-md ay-z-20">
              <FaSpinner className="ay-animate-spin ay-mr-2" />
              <span className="ay-text-sm">
                {isLoadingPreset
                  ? "Caricamento preset..."
                  : "Caricamento prodotti..."}
              </span>
            </div>
          )}

          {/* Toggle button - + rotates to x (desktop only) - matching App.js */}
          {!loading && (
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`ay-fixed ay-top-5 ay-right-5 ay-z-[60] ay-w-11 ay-h-11 ay-hidden md:ay-flex ay-items-center ay-justify-center ay-rounded-full ay-transition-all ay-duration-200 ${
                isSidebarOpen
                  ? "ay-bg-gray-200 hover:ay-bg-gray-300"
                  : "ay-bg-white hover:ay-bg-gray-200"
              }`}
              title={isSidebarOpen ? "Chiudi" : "Apri configuratore"}
            >
              <svg
                className="ay-w-5 ay-h-5 ay-text-gray-600 ay-transition-transform ay-duration-300 ay-ease-out"
                style={{
                  transform: isSidebarOpen ? "rotate(45deg)" : "rotate(0deg)",
                }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
            </button>
          )}

          {/* Desktop Sidebar Panel - matching App.js */}
          {!loading && (
            <div
              className="ay-fixed ay-top-0 ay-right-0 ay-h-screen ay-w-1/3 ay-bg-white ay-shadow-2xl ay-z-50 ay-flex-col ay-px-4 ay-hidden md:ay-flex"
              style={{
                transform: isSidebarOpen ? "translateX(0)" : "translateX(100%)",
                transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {/* Sidebar content - scrollable with padding */}
              <div className="ay-flex-1 ay-overflow-y-auto ay-scrollbar-hide">
                <div className="ay-pt-14">
                  {/* Category Selector - CATEGORIA */}
                  <PlannerCategorySelector
                    selectedCategory={selectedCategory}
                    onCategoryChange={handleCategoryChange}
                    language={language}
                  />

                  {/* Product Selector (Planner Family Accordion) - MODELLO */}
                  <PlannerFamilyAccordion
                    products={allProducts}
                    selectedCategory={selectedCategory}
                    onProductAdd={handleProductToAdd}
                    onProductDrag={handleDragStart}
                    language={language}
                    emptyImageRef={emptyImageRef}
                  />

                  {/* Products added to scene */}
                  <div className="ay-py-4">
                    <PlannerProductsList
                      productNodes={productNodes}
                      selectProductNodeInstance={selectProductNodeInstance}
                      removeProductNodeInstance={removeProductNodeInstance}
                      duplicateProductNode={duplicateProductNode}
                      rotateProductNodeInstance={rotateProductNodeInstance}
                      changeProductNodeInstanceHeight={
                        changeProductNodeInstanceHeight
                      }
                      selectedProductLocalId={selectedProductLocalId}
                      onSelectProduct={(localId) => {
                        setSelectedProductLocalId(localId)
                      }}
                      applyMaterial={applyMaterial}
                      language={language}
                    />
                  </div>
                </div>
              </div>

              {/* Sidebar footer */}
              <div className="ay-border-t ay-border-gray-300 ay-py-5 ay-bg-white">
                {/* Export/Import Actions */}
                <PlannerActions
                  productNodes={productNodes}
                  resetScene={resetScene}
                  iframeRef={iframeRef}
                  setLoadingProducts={setLoadingProducts}
                  setProductNodes={setProductNodes}
                  setProductNodesToAdd={setProductNodesToAdd}
                  nodeLocalIdsRef={nodeLocalIdsRef}
                  floorMaterial={floorMaterial}
                  setFloorMaterial={setFloorMaterial}
                  plannerPlayerRef={plannerPlayerRef}
                />

                {/* Subtotal */}
                <div className="ay-flex ay-items-center ay-justify-between ay-mb-5">
                  <span className="ay-text-[10px] lg:ay-text-[12px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider">
                    SUBTOTALE
                  </span>
                  <span className="ay-text-2xl ay-font-sans ay-font-semibold ay-text-gray-900">
                    {Object.values(productNodes).length > 0
                      ? `${Object.values(productNodes)
                          .reduce(
                            (sum, pn) => sum + (pn.refData?.price || 0),
                            0,
                          )
                          .toLocaleString("it-IT", {
                            minimumFractionDigits: 2,
                          })} EUR`
                      : "0,00 EUR"}
                  </span>
                </div>

                {/* Add to Cart Button */}
                <button className="ay-w-full ay-bg-black ay-text-white ay-py-3.5 ay-px-6 ay-rounded-full ay-font-semibold ay-font-serif ay-transition-colors ay-duration-300 hover:ay-bg-gray-800 ay-text-xs ay-uppercase ay-tracking-widest">
                  AGGIUNGI AL CARRELLO
                </button>
              </div>
            </div>
          )}

          {/* Mobile Bottom Sheet - matching App.js */}
          {/* Chevron button - positioned at bottom of player area */}
          {!loading && (
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="ay-fixed ay-left-1/2 ay-transform ay--translate-x-1/2 ay-w-10 ay-h-10 ay-bg-gray-100 ay-rounded-full ay-flex ay-items-center ay-justify-center ay-z-[60] md:ay-hidden ay-transition-all ay-duration-500 ay-ease-out"
              style={{
                bottom: isSidebarOpen
                  ? "calc(65vh - 20px)"
                  : "calc(5vh - 20px)",
              }}
            >
              <svg
                className={`ay-w-5 ay-h-5 ay-text-gray-600 ay-transition-transform ay-duration-300 ${
                  isSidebarOpen ? "ay-rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 15l7-7 7 7"
                />
              </svg>
            </button>
          )}

          {/* Bottom sheet content */}
          {!loading && (
            <div
              className="ay-fixed ay-inset-x-0 ay-bottom-0 ay-z-50 md:ay-hidden ay-bg-white ay-shadow-2xl ay-transition-all ay-duration-500 ay-ease-out ay-overflow-hidden ay-rounded-t-2xl"
              style={{
                height: isSidebarOpen ? "65vh" : "0",
              }}
            >
              <div className="ay-h-[65vh] ay-overflow-y-auto ay-scrollbar-hide ay-px-4 ay-pt-6">
                {/* Category Selector */}
                <PlannerCategorySelector
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                  language={language}
                />

                {/* Product Selector */}
                <PlannerFamilyAccordion
                  products={allProducts}
                  selectedCategory={selectedCategory}
                  onProductAdd={handleProductToAdd}
                  onProductDrag={handleDragStart}
                  language={language}
                  emptyImageRef={emptyImageRef}
                />

                {/* Products added to scene */}
                <div className="ay-py-4">
                  <PlannerProductsList
                    productNodes={productNodes}
                    selectProductNodeInstance={selectProductNodeInstance}
                    removeProductNodeInstance={removeProductNodeInstance}
                    duplicateProductNode={duplicateProductNode}
                    rotateProductNodeInstance={rotateProductNodeInstance}
                    changeProductNodeInstanceHeight={
                      changeProductNodeInstanceHeight
                    }
                    selectedProductLocalId={selectedProductLocalId}
                    onSelectProduct={(localId) => {
                      setSelectedProductLocalId(localId)
                      if (localId && productNodes[localId]) {
                        selectProductNodeInstance(productNodes[localId])()
                      }
                    }}
                    applyMaterial={applyMaterial}
                    language={language}
                  />
                </div>

                {/* Subtotal and cart button */}
                <div className="ay-py-5 ay-border-t ay-border-gray-300 ay-mt-4">
                  <div className="ay-flex ay-items-center ay-justify-between ay-mb-4">
                    <span className="ay-text-[10px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider">
                      SUBTOTALE
                    </span>
                    <span className="ay-text-2xl ay-font-sans ay-font-semibold ay-text-gray-900">
                      {Object.keys(productNodes).length > 0
                        ? `${(Object.keys(productNodes).length * 890).toLocaleString("it-IT", { minimumFractionDigits: 2 })} EUR`
                        : "0,00 EUR"}
                    </span>
                  </div>
                  <button className="ay-w-full ay-bg-black ay-text-white ay-py-3.5 ay-px-6 ay-rounded-full ay-font-semibold ay-font-serif ay-transition-colors ay-duration-300 hover:ay-bg-gray-800 ay-text-xs ay-uppercase ay-tracking-widest">
                    AGGIUNGI AL CARRELLO
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Planner
